/* eslint-disable no-console */
/* eslint-disable indent */
/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { scriptGraph, ScriptEngine } from '../scriptGraph';
import { firebaseConfig, getArtifactProjectId } from '../firebaseInit';
import { normalizeEmailToDocId } from '../utils/emailNormalization';
import * as firestorePaths from '../utils/firestorePaths';
import { logCriticalAction } from '../globalSetup';
import { useNotification } from '../contexts/NotificationContext';
import LoadingSpinner from './shared/LoadingSpinner';
import ErrorMessage from './shared/ErrorMessage';
import Button from './shared/Button';

// Default prospect form configuration (defined outside component to prevent recreation on every render)
const defaultProspectForm = {
  title: 'Lead Data',
  fields: [
    { id: 'agentName', label: 'Agent Name', type: 'text', placeholder: 'Enter your name', required: true },
    { id: 'prospectName', label: 'Contact Name', type: 'text', placeholder: 'Enter contact\'s full name', required: true },
    { id: 'company', label: 'Company', type: 'text', placeholder: 'Enter company name', required: false },
    { id: 'prospectPhone', label: 'Phone Number', type: 'tel', placeholder: 'Enter phone number', required: true }
  ]
};

/**
 * CallScript Component
 * Handles the call script interface including:
 * - Script progression through nodes
 * - Conversation history tracking
 * - Lead data collection
 * - Template selection
 */
const CallScript = ({
  // Authentication & User
  userId,
  userEmail,
  userRole,
  userTeam,
  userTeamName,
  isAdmin,
  isTeamLead,
  isAuthReady,
  db,
    
  // Templates
  availableScripts,
  setAvailableScripts,
  currentScriptTemplate,
  setCurrentScriptTemplate,
  defaultTemplate,
  templatesLoading,
  handleScriptTemplateChange,
  setAsDefaultTemplate,
    
  // Analytics
  analytics,
  currentCallSessionId,
  setCurrentCallSessionId,
    
  // Permissions
  canSwitchTemplates,
    
  // Other
  pendingTemplateAssignment,
  setPendingTemplateAssignment,
  isTemplateReady,
  setIsTemplateReady,
    
  // Team lead capture form (configured on Leads page)
  teamLeadCaptureForm,
  teamSubscriptionPlan
}) => {
  // Use notification system (replaces alert())
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
    
  // Call script state
  const [currentStage, setCurrentStage] = useState('opening');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [propertyDetails, setPropertyDetails] = useState({});
  const [urgency, setUrgency] = useState('Low');
  const [leadCategory, setLeadCategory] = useState('Nurture Campaign');
  const [callOutcome, setCallOutcome] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [prospectInput, setProspectInput] = useState('');
  const [multipleInputs, setMultipleInputs] = useState({});
  const [previousStages, setPreviousStages] = useState([]);
  const [currentStageConfig, setCurrentStageConfig] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [justSubmittedLead, setJustSubmittedLead] = useState(false);
  const [leadSaving, setLeadSaving] = useState(false);
    
  // Prospect data form state (shown after call ends)
  const [prospectDataForm, setProspectDataForm] = useState({
    prospectName: '',
    prospectAddress: '',
    prospectPhone: '',
    agentName: ''
  });
    
  // Current prospect form configuration from template
  const [currentProspectFormConfig, setCurrentProspectFormConfig] = useState(defaultProspectForm);
  const [prospectDataError, setProspectDataError] = useState('');
  const [rightPanelContent, setRightPanelContent] = useState('script'); // 'script', 'prospectData', or 'callSummary'
    
  // Conversation history layout preference: 'top' or 'side'
  const [historyLayout, setHistoryLayout] = useState(() => {
    try {
      const saved = localStorage.getItem(`historyLayout_${userId}`);
      return saved === 'side' ? 'side' : 'top';
    } catch { return 'top'; }
  });
    
  // Refs
  const conversationEndRef = useRef(null);
    
  // Toggle conversation history layout and persist preference
  const toggleHistoryLayout = useCallback(() => {
    setHistoryLayout(prev => {
      const next = prev === 'top' ? 'side' : 'top';
      try { localStorage.setItem(`historyLayout_${userId}`, next); } catch (e) { /* ignore localStorage errors */ }
      return next;
    });
  }, [userId]);
    
  // Calculate step progress from the script graph
  const stepProgress = useMemo(() => {
    if (!currentScriptTemplate || !availableScripts[currentScriptTemplate]) {
      return { current: 0, total: 0 };
    }
    const template = availableScripts[currentScriptTemplate];
    const graph = template?.graph || template?.data?.scriptGraph;
    if (!graph?.nodes) return { current: 0, total: 0 };
    // Count non-objection, non-error nodes as "steps"
    const nodeIds = Object.keys(graph.nodes).filter(
      id => !id.startsWith('objection_') && id !== '__end_script__' && id !== '__error__' && id !== 'call_end'
    );
    const total = nodeIds.length;
    const current = Math.min(previousStages.length + 1, total);
    return { current, total };
  }, [currentScriptTemplate, availableScripts, previousStages.length]);
    
  // Helper function to create a safe document ID from email
  const getEmailDocumentId = useCallback((email) => {
    if (!email) return null;
    return normalizeEmailToDocId(email);
  }, []);
    
  // Apply prospect form configuration when template changes
  // Priority: script-specific form (Business only) → team-wide form → default
  useEffect(() => {
    // 1. Business: Check for script-specific form (must have actual fields)
    if (teamSubscriptionPlan === 'pro_plus' && currentScriptTemplate && availableScripts[currentScriptTemplate]?.prospectForm?.fields?.length > 0) {
      console.log('✅ Business: Using script-specific prospect form');
      setCurrentProspectFormConfig(availableScripts[currentScriptTemplate].prospectForm);
    }
    // 2. Team-wide form (configured on Leads page)
    else if (teamLeadCaptureForm && teamLeadCaptureForm.fields?.length > 0) {
      console.log('✅ Using team-wide lead capture form');
      setCurrentProspectFormConfig(teamLeadCaptureForm);
    }
    // 3. Default fallback
    else {
      console.log('⚠️ No custom form configured, using default');
      setCurrentProspectFormConfig(defaultProspectForm);
    }
  }, [currentScriptTemplate, availableScripts, teamLeadCaptureForm, teamSubscriptionPlan]);
    
  // Initialize currentStage to actual opening node from template
  useEffect(() => {
    if (currentScriptTemplate && availableScripts[currentScriptTemplate] && currentStage === 'opening') {
      const template = availableScripts[currentScriptTemplate];
      const currentGraph = template?.graph || template?.data?.scriptGraph || scriptGraph;
            
      if (currentGraph?.nodes) {
        // Find the node marked as opening node
        const availableNodes = Object.keys(currentGraph.nodes);
        const openingNode = availableNodes.find(id => currentGraph.nodes[id].isOpeningNode);
        const actualOpeningNode = openingNode || availableNodes[0];
                
        // Only update if the actual opening node is different from 'opening'
        if (actualOpeningNode && actualOpeningNode !== 'opening' && !currentGraph.nodes['opening']) {
          console.log(`🔄 Initializing to actual opening node: "${actualOpeningNode}"`);
          setCurrentStage(actualOpeningNode);
        }
      }
    }
  }, [currentScriptTemplate, availableScripts]); // Removed currentStage to prevent infinite loop
    
  // Scroll to bottom of conversation
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [conversationHistory]);
    
  // Load current stage configuration
  useEffect(() => {
    if (!currentScriptTemplate || !availableScripts[currentScriptTemplate]) {
      console.log('⚠️ No template selected or template not found');
      return;
    }
        
    const template = availableScripts[currentScriptTemplate];
    let currentGraph = template?.graph || template?.data?.scriptGraph || scriptGraph;
        
    if (!currentGraph || !currentStage) {
      console.log('⚠️ No graph or current stage');
      return;
    }
        
    // Handle error state - when no valid nodes exist or recovery failed
    if (currentStage === '__error__') {
      setCurrentStageConfig({
        script: 'We\'re experiencing a technical issue with this script template. Please contact support for assistance.',
        coaching: 'Technical error - script template is corrupted or missing.',
        buttons: [{
          label: 'End Call and Report Issue',
          value: 'end_call',
          nextStage: 'call_end',
          type: 'predetermined'
        }],
        requiresTextInput: false,
        isError: true
      });
      return;
    }
        
    // Special handling for objection nodes - ensure they have proper button configuration
    if (currentStage?.startsWith('objection_')) {
      console.log('🔍 Objection node requested:', currentStage, {
        nodeExists: !!currentGraph?.nodes?.[currentStage],
        allNodeIds: Object.keys(currentGraph?.nodes || {}),
        objectionNodeIds: Object.keys(currentGraph?.nodes || {}).filter(id => id.startsWith('objection_'))
      });
            
      if (currentGraph?.nodes?.[currentStage]) {
        const objectionNode = currentGraph.nodes[currentStage];
        console.log('✅ Found objection node:', currentStage, {
          id: objectionNode.id,
          script: objectionNode.script?.substring?.(0, 100) + '...',
          hasResponses: !!objectionNode.responses,
          responseKeys: Object.keys(objectionNode.responses || {})
        });
                
        // Only inject continue/end_call if the objection node has NO usable responses.
        // AI-generated and generic objection nodes already have their own complete
        // response flows (e.g. agree_meeting → close_appointment), so injecting
        // continue/end_call would create duplicate/confusing buttons.
        const existingResponses = objectionNode.responses ? Object.keys(objectionNode.responses) : [];
        const hasUsableResponses = existingResponses.length > 0 &&
          existingResponses.some(key => {
            const r = objectionNode.responses[key];
            return r && r.nextNode && r.nextNode !== '__return_to_previous__' && r.nextNode !== '__end_script__';
          });

        if (!objectionNode.responses || existingResponses.length === 0) {
          console.warn('⚠️ Objection node has no responses, adding continue/end_call:', currentStage);
          const updatedGraph = {
            ...currentGraph,
            nodes: {
              ...currentGraph.nodes,
              [currentStage]: {
                ...objectionNode,
                responses: {
                  continue: {
                    nextNode: '__return_to_previous__',
                    label: 'Continue with question',
                    data: {}
                  },
                  end_call: {
                    nextNode: '__end_script__',
                    label: 'End Call',
                    data: {}
                  }
                }
              }
            }
          };
                    
          setAvailableScripts(prev => ({
            ...prev,
            [currentScriptTemplate]: {
              ...prev[currentScriptTemplate],
              graph: updatedGraph
            }
          }));
                    
          currentGraph = updatedGraph;
        } else if (!hasUsableResponses && !objectionNode.responses.continue) {
          // Node only has __return_to_previous__ or __end_script__ responses,
          // but is missing the continue key — add it as a safety net
          console.warn('⚠️ Objection node has no forward-navigating responses, adding continue/end_call:', currentStage);
          const updatedGraph = {
            ...currentGraph,
            nodes: {
              ...currentGraph.nodes,
              [currentStage]: {
                ...objectionNode,
                responses: {
                  ...objectionNode.responses,
                  continue: {
                    nextNode: '__return_to_previous__',
                    label: 'Continue with question',
                    data: {}
                  },
                  end_call: {
                    nextNode: '__end_script__',
                    label: 'End Call',
                    data: {}
                  }
                }
              }
            }
          };
                    
          setAvailableScripts(prev => ({
            ...prev,
            [currentScriptTemplate]: {
              ...prev[currentScriptTemplate],
              graph: updatedGraph
            }
          }));
                    
          currentGraph = updatedGraph;
        } else {
          console.log('✅ Objection node already has complete response flow, skipping continue/end_call injection:', currentStage, existingResponses);
        }
      } else {
        console.error('❌ Objection node NOT FOUND in graph:', currentStage);
        console.error('Available objection nodes:', Object.keys(currentGraph?.nodes || {}).filter(id => id.startsWith('objection_')));
        console.error('Template steps:', template?.steps?.map(s => ({
          id: s.id,
          objections: s.objectionResponses?.map(o => `objection_${o.id}`)
        })));
                
        // Fallback: return error config
        setCurrentStageConfig({
          script: 'Error: Objection handling not found. Please save your script in Script Editor first.',
          coaching: 'Objection node is missing from the script graph.',
          buttons: [{
            label: 'Return to previous question',
            value: 'continue',
            nextStage: '__return_to_previous__',
            type: 'predetermined'
          }],
          requiresTextInput: false,
          isError: true
        });
        return;
      }
    }
        
    const currentEngine = new ScriptEngine(currentGraph);

    // Check if the current stage exists in the graph BEFORE loading config
    // If not, redirect to the correct opening node to prevent stale stage issues
    const nodeExists = currentStage === '__end_script__' || !!currentGraph.nodes[currentStage];
    if (!nodeExists && !currentStage?.startsWith('objection_') && currentStage !== 'opening' && currentStage !== 'call_end' && currentStage !== '__error__') {
      console.log(`⚠️ Stage "${currentStage}" not found in current template, redirecting...`);
      const availableNodes = Object.keys(currentGraph.nodes || {});
      const openingNode = availableNodes.find(id => currentGraph.nodes[id].isOpeningNode);
      const fallbackNode = openingNode || availableNodes[0];
      if (fallbackNode) {
        console.log(`🔄 Correcting currentStage from "${currentStage}" to "${fallbackNode}"`);
        setCurrentStage(fallbackNode);
      }
      return; // Let the effect re-fire with the corrected stage
    }

    const config = currentEngine.getNodeConfig(currentStage, propertyDetails);
        
    setCurrentStageConfig(config);
        
    // Mark template as ready once we have a valid configuration
    if (config && !isTemplateReady) {
      console.log('✅ Template ready - stage config loaded:', currentStage);
      setIsTemplateReady(true);
    }
  }, [currentStage, currentScriptTemplate, availableScripts, propertyDetails, isTemplateReady]);
  // Note: setState functions (setIsTemplateReady, setAvailableScripts) removed from dependencies as they shouldn't change
    
  // Handle multiple open text responses
  const handleMultipleOpenTextResponses = (openTextButtons, multipleInputs) => {
    console.log('🔄 Handling multiple open text responses:', { openTextButtons, multipleInputs });
        
    // Validate all required inputs
    const missingInputs = openTextButtons.filter(button => {
      const input = multipleInputs[button.value];
      return !input || input.trim() === '';
    });
        
    if (missingInputs.length > 0) {
      showWarning(`Please fill in all required fields: ${missingInputs.map(b => b.label).join(', ')}`);
      return;
    }
        
    const template = availableScripts?.[currentScriptTemplate];
    const currentGraph = template?.graph || template?.data?.scriptGraph;
    const currentEngine = new ScriptEngine(currentGraph);
        
    // Add all responses to conversation history
    const conversationUpdates = [];
        
    // Add the agent's script
    if (currentStageConfig?.script) {
      conversationUpdates.push({
        role: 'Agent',
        text: typeof currentStageConfig.script === 'function' 
          ? currentStageConfig.script(propertyDetails) 
          : currentStageConfig.script
      });
    }
        
    // Add each prospect response
    openTextButtons.forEach(button => {
      const inputValue = multipleInputs[button.value];
      if (inputValue && inputValue.trim()) {
        conversationUpdates.push({
          role: 'Prospect',
          text: `${button.label}: ${inputValue}`
        });
      }
    });
        
    setConversationHistory(prev => [...prev, ...conversationUpdates]);
        
    // Update property details with collected data
    const newPropertyDetails = { ...propertyDetails };
    openTextButtons.forEach(button => {
      const inputValue = multipleInputs[button.value];
      if (inputValue && button.dataField) {
        newPropertyDetails[button.dataField] = inputValue;
      }
    });
    setPropertyDetails(newPropertyDetails);
        
    // Use the first button's nextStage (they should all have the same nextStage)
    const nextNode = openTextButtons[0]?.nextStage;
        
    if (nextNode) {
      console.log('🎯 Moving to next node:', nextNode);
            
      // Track stage transition
      setPreviousStages(prev => [...prev, currentStage]);
            
      // Track node visit for analytics
      if (analytics && currentCallSessionId) {
        analytics.trackCallEvent('node_visited', {
          sessionId: currentCallSessionId,
          template: currentScriptTemplate,
          fromNode: currentStage,
          toNode: nextNode,
          responseType: 'multiple_text',
          response: 'multiple_text_inputs'
        });
      }
            
      setCurrentStage(nextNode);
    } else {
      console.log('❌ No next node found for multiple open text responses');
    }
        
    // Clear all multiple inputs
    setMultipleInputs({});
  };
    
  // Handle prospect response
  const handleProspectResponse = (response, type = 'button') => {
    // Initialize variables at the top to avoid temporal dead zone issues
    let prospectText = '';
    let nextNode;
    let conversationUpdates = [];
        
    // Get current graph early for objection handling
    const template = availableScripts?.[currentScriptTemplate];
    const currentGraph = template?.graph || template?.data?.scriptGraph;
        
    // Handle special cases first
    if (response === 'back') {
      if (previousStages.length > 0) {
        const newPreviousStages = [...previousStages];
        const previousStage = newPreviousStages.pop();
        setPreviousStages(newPreviousStages);
        setCurrentStage(previousStage);
                
        // Remove the last two entries from conversation history (agent script + prospect response)
        setConversationHistory(prev => {
          const newHistory = [...prev];
          // Remove the last two entries if they exist
          if (newHistory.length >= 2) {
            newHistory.splice(-2);
          }
          return newHistory;
        });
                
        // Clear any text input
        setProspectInput('');
        return;
      }
      return;
    }

    if (response === 'end_call' || response === 'end_call_no_notes') {
      setCurrentStage('call_end');
      setRightPanelContent('prospectData');
      setProspectInput('');
      return;
    }

    // Handle objection responses
    if (type === 'objection' && response.startsWith('objection_') && availableScripts && currentScriptTemplate) {
      const objectionId = response.replace('objection_', '');
      const objectionNodeId = `objection_${objectionId}`;
            
      console.log('🔍 OBJECTION DEBUG: Handling objection selection', {
        objectionId,
        objectionNodeId,
        currentStage,
        previousStagesBeforeAdd: [...previousStages],
        previousStagesLength: previousStages.length
      });
            
      // Find the objection in the current step
      const currentStep = availableScripts[currentScriptTemplate]?.steps?.find(step => step.id === currentStageConfig?.step);
      const objection = currentStep?.objectionResponses?.find(obj => obj.id === objectionId);
            
      if (objection) {
        // Reset conversation updates array for objection handling
        conversationUpdates = [];
                
        // Add the agent's script to conversation history
        if (currentStageConfig?.script) {
          conversationUpdates.push({
            role: 'Agent',
            text: typeof currentStageConfig.script === 'function' 
              ? currentStageConfig.script(propertyDetails) 
              : currentStageConfig.script
          });
        }
                
        // Add the prospect's objection to conversation history
        conversationUpdates.push({
          role: 'Prospect',
          text: objection.label
        });
                
        console.log('🔍 OBJECTION DEBUG: Adding to previousStages before objection', {
          currentStage,
          previousStagesBeforeAdd: [...previousStages],
          aboutToAdd: currentStage
        });
                
        // Add current stage to previous stages BEFORE transitioning to objection
        setPreviousStages(prev => {
          const newStages = [...prev, currentStage];
          console.log('🔍 OBJECTION DEBUG: Updated previousStages', {
            old: prev,
            new: newStages,
            added: currentStage
          });
          return newStages;
        });
                
        // Update conversation history
        setConversationHistory(prev => [...prev, ...conversationUpdates]);
                
        // Transition to objection node
        console.log('🔍 OBJECTION DEBUG: Transitioning to objection node', objectionNodeId);
        setCurrentStage(objectionNodeId);
                
        // Clear input
        setProspectInput('');
        return;
      } else {
        console.error('❌ Objection not found:', objectionId);
        return;
      }
    }
        
    // Normal response handling
    const currentEngine = new ScriptEngine(currentGraph || {});
        
    // Add the agent's script to conversation history (if not already added)
    if (currentStageConfig?.script && type !== 'objection') {
      conversationUpdates.push({
        role: 'Agent',
        text: typeof currentStageConfig.script === 'function' 
          ? currentStageConfig.script(propertyDetails) 
          : currentStageConfig.script
      });
    }
        
    // Add the prospect's response to conversation history
    if (type === 'text' && prospectInput.trim()) {
      prospectText = prospectInput.trim();
    } else if (type === 'button' || type === 'predetermined') {
      console.log('Processing button response:', response);
      console.log('Button type:', type);
            
      // Get the button label from the current stage config
      const currentButton = currentStageConfig?.buttons?.find(button => button.value === response);
      if (currentButton && currentButton.label) {
        prospectText = currentButton.label;
        console.log('✅ Found button label:', prospectText);
      } else {
        // Fallback to hardcoded map for common responses
        const responseMap = {
          'yes': 'Yes, I\'m open to selling',
          'not_interested': 'No, not interested',
          'already_have_agent': 'We already have an agent',
          'wrong_time': 'Wrong time',
          'money_priority': 'Getting the Most Money',
          'timing_priority': 'Timing is Most Important',
          'both_important': 'Both are important',
          'market_not_right': 'Market isn\'t right',
          'vacant': 'Vacant',
          'rented': 'Rented',
          'owner_occupied': 'Owner Occupied',
          'renovations': 'Planning renovations',
          'thinking_selling': 'Thinking of Selling',
          'submit_rent': prospectInput.trim() || 'Submit Amount',
          'annual': 'Annual Lease',
          'month_to_month': 'Month-to-Month',
          'tenants_staying': 'Tenants Staying',
          'vacant_closing': 'Deliver Vacant at Closing',
          'may_retain': 'Yes, may retain',
          'will_sell': 'No, will sell',
          'need_more_time': 'Need more time',
          'not_sure_timing': 'Not sure if now is right time',
          'open_acceleration': 'Yes, open to acceleration',
          'not_open_acceleration': 'No, not open',
          'higher_prices': 'Higher prices than today',
          'faster_sale': 'Faster sale process',
          'less_hassle': 'Less hassle/stress involved',
          'life_event': 'A specific life event',
          'just_curious': 'Just curious, not really selling',
          'actively_listed': 'Actively listed',
          'agent_future': 'Have an agent for future plans',
          'friend_family': 'Just a friend/family in real estate',
          'not_serious': 'Not serious about selling right now',
          'market_improve': 'Market needs to improve',
          'personal_reasons': 'Personal/family reasons',
          'job_uncertainty': 'Job/relocation uncertainty',
          'property_repairs': 'Property needs repairs/updates',
          'no_specific': 'No specific reason, just not now',
          'reconsider_yes': 'Yes, would reconsider',
          'reconsider_no': 'No, still hesitant',
          'need_more_info': 'Need more information/data',
          'schedule_tuesday': 'Tuesday evening works',
          'schedule_thursday': 'Thursday afternoon works',
          'another_time': 'Another time',
          'submit_time': prospectInput.trim() || 'Submit Time',
          'submit_notes': prospectInput.trim() || 'Submit Notes',
          'end_call': 'End Call & Save',
          'end_call_no_notes': 'End Call & Save',
          'back': 'Go back to previous stage'
        };
        const mappedText = responseMap[response];
        prospectText = mappedText || response;
                
        if (!mappedText) {
          console.log('⚠️ Response not found in button config or fallback map:', response);
          console.log('Available buttons:', currentStageConfig?.buttons?.map(b => ({ value: b.value, label: b.label })));
        }
      }
            
      console.log('Response mapping debug:', {
        response,
        buttonLabel: currentButton?.label,
        finalProspectText: prospectText,
        availableButtons: currentStageConfig?.buttons?.length || 0
      });
    }

    console.log('Button response debug:', {
      response,
      type,
      prospectText,
      hasProspectText: !!prospectText
    });

    if (prospectText) {
      console.log('✅ Adding button response to conversation:', prospectText);
      conversationUpdates.push({
        role: 'Prospect',
        text: prospectText
      });
    } else {
      console.log('❌ No prospect text to add for button response');
    }

    // Update conversation history with all updates at once
    if (conversationUpdates.length > 0) {
      setConversationHistory(prev => {
        const newHistory = [...prev, ...conversationUpdates];
        console.log('New conversation history length:', newHistory.length);
        console.log('Added entries:', conversationUpdates.length);
        return newHistory;
      });
    }

    // Update property details based on response using script engine
    const newPropertyDetails = { ...propertyDetails };
        
    // Get the current node and response data from script engine
    const currentNode = currentEngine.getNode(currentStage);
    const responseData = currentNode?.responses[response]?.data;
        
    // Update property details with response data
    if (responseData) {
      Object.assign(newPropertyDetails, responseData);
    }
        
    // Handle text input data
    if (type === 'text' && prospectInput.trim()) {
      // Check if current stage has open text responses
      const openTextButton = currentStageConfig?.buttons?.find(button => button.type === 'open_text');
            
      if (openTextButton && openTextButton.dataField) {
        // Store the text input in the specified data field
        newPropertyDetails[openTextButton.dataField] = prospectInput.trim();
      } else {
        // Fallback to stage-specific handling
        switch (currentStage) {
          case 'rent_amount':
            newPropertyDetails.rentAmount = prospectInput.trim();
            break;
          case 'closing_schedule_other':
            newPropertyDetails.appointmentDate = prospectInput.trim();
            break;
          case 'closing_notes':
            newPropertyDetails.additionalNotes = prospectInput.trim();
            break;
          default:
            // No specific handling for other stages
            break;
        }
      }
    }

    setPropertyDetails(newPropertyDetails);
        
    // Find next stage using script engine
    // (template and currentGraph already defined at top of function)
        
    if (!currentGraph || !currentGraph.nodes) {
      console.warn('⚠️ No valid graph found for template:', currentScriptTemplate);
      console.warn('   Template structure:', template ? Object.keys(template) : 'template is null');
    }
        
    nextNode = currentEngine.getNextNode(currentStage, response, newPropertyDetails);
        
    console.log('🎯 Final nextNode:', nextNode);
        
    // Handle missing node scenario - if current node doesn't exist, reset to valid start
    if (!nextNode && !currentEngine.getNode(currentStage)) {
      console.log('❌ Current node not found in script graph, attempting recovery...');
            
      // Find the first valid node or designated opening node
      const availableNodes = Object.keys(currentGraph.nodes || {});
      const openingNode = availableNodes.find(id => currentGraph.nodes[id].isOpeningNode);
      const fallbackNode = openingNode || availableNodes[0];
            
      if (fallbackNode) {
        console.log(`🔄 Recovering from invalid node "${currentStage}" to "${fallbackNode}"`);
                
        // Log this critical error for debugging
        logCriticalAction('ERROR', 'Invalid node recovery', {
          invalidNode: currentStage,
          recoveryNode: fallbackNode,
          template: currentScriptTemplate,
          availableNodes: availableNodes.length,
          userEmail: userEmail,
          timestamp: new Date().toISOString()
        });
                
        // Reset to the valid node
        setCurrentStage(fallbackNode);
        setProspectInput('');
                
        // Add a recovery message to conversation history
        conversationUpdates.push({
          role: 'System',
          text: 'Restarting conversation flow...'
        });
                
        // Update conversation history and exit early
        setConversationHistory(prev => [...prev, ...conversationUpdates]);
        return;
      } else {
        console.error('❌ No valid nodes found in script template');
        // If no valid nodes exist, show an error state
        setCurrentStage('__error__');
        return;
      }
    }
        
    // Handle special objection response - return to previous question
    if (nextNode === '__stay_current__' || nextNode === '__return_to_previous__') {
      console.log('🔍 RETURN DEBUG: Handling return from objection', {
        nextNode,
        currentStage,
        isObjectionNode: currentStage?.startsWith('objection_'),
        previousStagesLength: previousStages.length,
        previousStages: [...previousStages]
      });
            
      // If we're on an objection node, go back to the last NON-OBJECTION stage
      if (currentStage?.startsWith('objection_') && previousStages.length > 0) {
        console.log('🔍 RETURN DEBUG: Finding last non-objection stage');
                
        // Find the last NON-OBJECTION stage in previousStages
        // This handles the case of multiple objections in a row (objection1 → objection2 → objection3)
        let targetStage = null;
        const newPreviousStages = [...previousStages];
        const poppedStages = [];
                
        while (newPreviousStages.length > 0) {
          const stage = newPreviousStages.pop();
          poppedStages.push(stage);
                    
          if (!stage.startsWith('objection_')) {
            targetStage = stage;
            console.log('🔍 RETURN DEBUG: Found non-objection stage:', stage);
            break;
          } else {
            console.log('🔍 RETURN DEBUG: Skipping objection stage:', stage);
          }
        }
                
        if (targetStage) {
          console.log('🔍 RETURN DEBUG: Returning to stage', {
            targetStage,
            poppedStages,
            remainingStages: newPreviousStages,
            currentObjectionNode: currentStage
          });
                    
          setPreviousStages(newPreviousStages);
          setCurrentStage(targetStage);
                    
          // Remove objection entries from conversation history
          // Calculate how many entries to remove: 2 per objection (agent response + prospect selection)
          const objectionCount = poppedStages.length; // Current objection + any previous objections we skipped
          const entriesToRemove = objectionCount * 2;
                    
          console.log('🔍 RETURN DEBUG: Removing conversation entries', {
            objectionCount,
            entriesToRemove
          });
                    
          setConversationHistory(prev => {
            const newHistory = [...prev];
            if (newHistory.length >= entriesToRemove) {
              newHistory.splice(-entriesToRemove);
            }
            return newHistory;
          });
        } else {
          // No non-objection stage found (shouldn't happen in normal flow)
          console.error('🔍 RETURN DEBUG: No non-objection stage found! Staying on current stage.');
        }
                
        setProspectInput('');
        return;
      } else {
        // For non-objection cases, add the response if it's meaningful
        // Ensure prospectText is defined and not the continue button text
        if (typeof prospectText === 'string' && prospectText && prospectText !== 'Continue with question') {
          setConversationHistory(prev => [...prev, {
            role: 'Prospect',
            text: prospectText
          }]);
        }
        setProspectInput('');
        return;
      }
    }
        
    if (nextNode) {
      logCriticalAction('ACTION', 'Stage transition', {
        fromStage: currentStage,
        toStage: nextNode,
        responseType: type,
        template: currentScriptTemplate,
        sessionId: currentCallSessionId,
        previousStagesCount: previousStages.length,
        isEndScript: nextNode === '__end_script__'
      });

      setPreviousStages(prev => [...prev, currentStage]);
            
      // Track node visit for analytics
      if (analytics && currentCallSessionId) {
        analytics.trackCallEvent('node_visited', {
          sessionId: currentCallSessionId,
          template: currentScriptTemplate,
          fromNode: currentStage,
          toNode: nextNode,
          responseType: type,
          response: type === 'text' ? 'text_input' : response
        });
      }
            
      setCurrentStage(nextNode);
            
      // Handle end script transition - automatically show prospect data collection
      if (nextNode === '__end_script__') {
        logCriticalAction('ACTION', 'End script reached - switching to prospect data', {
          fromStage: currentStage,
          sessionId: currentCallSessionId,
          template: currentScriptTemplate,
          totalStagesVisited: previousStages.length + 1
        });
        setRightPanelContent('prospectData');
      }
    } else {
      logCriticalAction('ERROR', 'No next stage found - attempting recovery', {
        currentStage: currentStage,
        response: type === 'text' ? '[TEXT_INPUT]' : response,
        responseType: type,
        template: currentScriptTemplate,
        availableResponses: currentStageConfig?.buttons?.map(b => b.response) || []
      });
            
      // Recovery strategy: Try to find a valid next step
      const availableNodes = Object.keys(currentGraph.nodes || {});
      const openingNode = availableNodes.find(id => currentGraph.nodes[id].isOpeningNode);
            
      // Try to find any node that connects to end script
      const endConnectedNode = Object.keys(currentGraph.nodes).find(id => {
        const node = currentGraph.nodes[id];
        return node.responses && Object.values(node.responses).some(resp => resp.nextNode === '__end_script__');
      });
            
      // Use opening node, or end-connected node, or just end the script
      if (openingNode && openingNode !== currentStage) {
        console.log(`🔄 Recovery: Redirecting to opening node: ${openingNode}`);
        setCurrentStage(openingNode);
      } else if (endConnectedNode && endConnectedNode !== currentStage) {
        console.log(`🔄 Recovery: Redirecting to end-connected node: ${endConnectedNode}`);
        setCurrentStage(endConnectedNode);
      } else {
        console.log('🔄 Recovery: Ending script due to broken template');
        setCurrentStage('__end_script__');
        setRightPanelContent('prospectData');
      }
    }
        
    // Clear inputs after processing
    setProspectInput('');
    setMultipleInputs({});
  };

  // Reset the application
  const resetApp = (isCompletion = false) => {
    logCriticalAction('ACTION', 'Application reset initiated', {
      isCompletion: isCompletion,
      currentStage: currentStage,
      currentSessionId: currentCallSessionId,
      conversationLength: conversationHistory.length,
      template: currentScriptTemplate,
      hasPropertyDetails: Object.keys(propertyDetails).length > 0
    });

    // Generate a new session ID for the upcoming call *before* tracking any events
    const newSessionId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Track appropriate event if there was an active call session
    if (analytics && currentCallSessionId && conversationHistory.length > 0) {
      // Only track completion here if a lead was NOT just submitted
      if (isCompletion && !justSubmittedLead) {
        // This is a "Start New Call" after successful completion
        console.log('🎯 Tracking call_complete with template:', currentScriptTemplate);
        analytics.trackCallEvent('call_complete', {
          sessionId: currentCallSessionId, // Use the ID of the call that is ending
          template: currentScriptTemplate,
          finalNode: currentStage,
          stepsCompleted: conversationHistory.length,
          completionType: 'manual_new_call'
        });
      } else if (!isCompletion) {
        // This is a true reset/abandonment
        console.log('🔄 Tracking call_reset with template:', currentScriptTemplate);
        analytics.trackCallEvent('call_reset', {
          sessionId: currentCallSessionId, // Use the ID of the call that is being reset
          template: currentScriptTemplate,
          nodeId: currentStage,
          stepsCompleted: conversationHistory.length,
          resetReason: 'user_initiated'
        });
      }
    }
        
    // Now, set the new session ID for the call that is about to begin
    setCurrentCallSessionId(newSessionId);
        
    // Set analytics session ID to match the new call session ID
    if (analytics) {
      analytics.setSessionId(newSessionId);
    }
        
    // Reset the lead submission flag for the new call
    setJustSubmittedLead(false);
        
    // Apply any pending template assignment now that the call has ended
    if (pendingTemplateAssignment && userRole === 'basic') {
      console.log(`🔄 Applying pending template assignment: ${pendingTemplateAssignment.templateId}`);
      setCurrentScriptTemplate(pendingTemplateAssignment.templateId);
            
      // Show notification about template switch
      setTimeout(() => {
        showInfo(`Template updated! "${pendingTemplateAssignment.templateName}" is now active.`, { duration: 5000 });
      }, 500); // Small delay to let the UI update
            
      // Clear the pending assignment
      setPendingTemplateAssignment(null);
    }
        
    // Track the start of the new call with the new session ID
    if (analytics) {
      // Use setTimeout to ensure currentScriptTemplate is up-to-date
      setTimeout(() => {
        console.log('🎯 Tracking call_start for new session:', newSessionId, 'with template:', currentScriptTemplate);
        analytics.trackCallEvent('call_start', {
          sessionId: newSessionId,
          template: currentScriptTemplate || 'example-script',
          startNode: 'opening'
        });
      }, 100);
    }
        
    // Reset all state
    setCurrentStage('opening');
    setConversationHistory([]);
    setPropertyDetails({});
    setUrgency('Low');
    setLeadCategory('Nurture Campaign');
    setCallOutcome('');
    setSaveStatus('');
    setProspectInput('');
    setMultipleInputs({});
    setPreviousStages([]);
    setProspectDataForm({
      prospectName: '',
      prospectAddress: '',
      prospectPhone: '',
      agentName: ''
    });
    setProspectDataError('');
    setRightPanelContent('script');
    setShowResetDialog(false);
  };
    
  // Save lead to Firestore with timeout protection and retry logic
  const saveLeadToFirestore = async (leadData, retryCount = 0) => {
    const maxRetries = 2; // Allow up to 2 retries (3 total attempts)
        
    logCriticalAction('DATA', 'Lead save initiated', {
      retryCount,
      hasDatabase: !!db,
      hasUserId: !!userId,
      isAuthReady,
      hasUserEmail: !!userEmail,
      userTeam: userTeam,
      leadDataKeys: Object.keys(leadData)
    });
        
    if (!db || !userId || !isAuthReady || !userEmail || !userTeam) {
      const errorMsg = !userTeam
        ? 'Your account is still setting up. Please wait a moment and try again.'
        : 'Error: Database not ready or user not authenticated. Cannot save lead.';
      logCriticalAction('ERROR', 'Lead save failed - prerequisites not met', {
        hasDatabase: !!db,
        hasUserId: !!userId,
        isAuthReady,
        hasUserEmail: !!userEmail,
        hasUserTeam: !!userTeam
      });
      setSaveStatus(errorMsg);
      return;
    }

    const attempt = retryCount + 1;
    const retryText = retryCount > 0 ? ` (Attempt ${attempt}/${maxRetries + 1})` : '';
    setSaveStatus(`Saving lead data...${retryText}`);
        
    try {
      const appId = getArtifactProjectId();
      const emailDocId = getEmailDocumentId(userEmail);
            
      // Save lead to user's leads collection within team container
      const leadsPath = firestorePaths.getLeadsPath(appId, userTeam, emailDocId);
      const leadsRef = collection(db, leadsPath);
            
      logCriticalAction('DATA', 'Saving lead to team container', {
        team: userTeam,
        path: leadsPath,
        attempt: attempt
      });
            
      await addDoc(leadsRef, leadData);
            
      logCriticalAction('SUCCESS', 'Lead saved to team container', {
        team: userTeam,
        leadDataKeys: Object.keys(leadData)
      });
            
      setSaveStatus('Lead saved successfully!');
            
      // Track lead_generated event for analytics (Script Performance counts this event type)
      if (analytics && currentCallSessionId) {
        console.log('📊 Tracking lead_generated event with session:', currentCallSessionId);
        analytics.trackCallEvent('lead_generated', {
          sessionId: currentCallSessionId,
          template: currentScriptTemplate,
          stepsCompleted: conversationHistory.length,
          urgency: leadData.urgency,
          leadCategory: leadData.leadCategory
        });
                
        // Mark that we just submitted a lead (for analytics tracking)
        setJustSubmittedLead(true);
      }
            
      // Auto-restart after successful save (2000ms delay for user feedback)
      setTimeout(() => {
        resetApp(true); // Pass true to indicate completion
      }, 2000);
            
    } catch (error) {
      logCriticalAction('ERROR', 'Lead save failed', {
        errorCode: error.code,
        errorMessage: error.message,
        attempt: attempt,
        maxRetries: maxRetries,
        team: userTeam
      });
            
      console.error('Error saving lead to Firestore:', error);
            
      // Handle permission-denied error specifically
      if (error.code === 'permission-denied') {
        setSaveStatus('Permission error: You don\'t have access to save leads. Please contact your team lead.');
        logCriticalAction('ERROR', 'Permission denied saving lead', {
          userEmail: userEmail,
          userTeam: userTeam,
          userRole: userRole
        });
      } else if (retryCount < maxRetries) {
        // Retry after a delay
        const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff, max 5s
        console.log(`⏳ Retrying lead save in ${retryDelay}ms...`);
        setTimeout(() => {
          saveLeadToFirestore(leadData, retryCount + 1);
        }, retryDelay);
      } else {
        setSaveStatus(`Error saving lead after ${maxRetries + 1} attempts: ${error.message}`);
      }
    }
  };
    
  // Handle lead data form submission
  const handleProspectDataSubmit = async () => {
    // Validate required fields
    const errors = [];
        
    currentProspectFormConfig.fields.forEach(field => {
      if (field.required && !prospectDataForm[field.id]?.trim()) {
        errors.push(field.label);
      }
    });
        
    if (errors.length > 0) {
      setProspectDataError(`Please fill in the following required fields: ${errors.join(', ')}`);
      return;
    }
        
    setLeadSaving(true);
    setProspectDataError('');
        
    // Prepare lead data
    const leadData = {
      ...prospectDataForm,
      conversationHistory: conversationHistory,
      propertyDetails: propertyDetails,
      urgency: urgency,
      leadCategory: leadCategory,
      callOutcome: callOutcome || '',
      timestamp: new Date(),
      createdBy: userId,
      userId: userId,
      userEmail: userEmail,
      creatorEmail: userEmail,
      creatorTeam: userTeam,
      userTeam: userTeam,
      userTeamName: userTeamName,
      template: currentScriptTemplate,
      templateName: availableScripts[currentScriptTemplate]?.name || 'Unknown Template'
    };
        
    // Save to Firestore
    await saveLeadToFirestore(leadData);
        
    setLeadSaving(false);
  };
    
  // Render
  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Show loading state until template is ready */}
      {!isTemplateReady && (
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner size="large" message="Loading your script template..." />
        </div>
      )}
            
      {/* Main content - only show when template is ready */}
      {isTemplateReady && (
        <>
          {/* Header with Template Selector - matching sidebar header style */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 h-14 bg-white">
            <h2 className="text-xl font-semibold text-blue-600 border-b-2 border-blue-600 pb-1">Call Script</h2>
                    
            {/* Template Selector */}
            <div className="flex flex-row items-center space-x-2">
              {canSwitchTemplates() ? (
                <>
                  <select
                    value={currentScriptTemplate || ''}
                    onChange={(e) => handleScriptTemplateChange(e.target.value)}
                    className="bg-white border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{ width: '260px', flexShrink: 0 }}
                  >
                    {Object.entries(availableScripts)
                      .map(([id, script]) => (
                        <option key={id} value={id}>
                          {script.name}
                        </option>
                      ))}
                  </select>
                  {templatesLoading && (
                    <LoadingSpinner size="small" inline />
                  )}
                  {/* Default star — always visible when editable, amber when default, gray when not */}
                  {userRole !== 'basic' && currentScriptTemplate && currentScriptTemplate !== 'example-script' && availableScripts[currentScriptTemplate] && !availableScripts[currentScriptTemplate]?.isReadOnly && (
                    currentScriptTemplate === defaultTemplate ? (
                      <span
                        title="This is your default script"
                        className="flex items-center justify-center w-7 h-7 text-amber-400 cursor-default flex-shrink-0"
                        aria-label="Default script"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </span>
                    ) : (
                      <button
                        onClick={() => setAsDefaultTemplate(currentScriptTemplate)}
                        title="Set as default script"
                        className="flex items-center justify-center w-7 h-7 text-gray-300 hover:text-amber-400 transition-colors flex-shrink-0 rounded"
                        aria-label="Set as default script"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 20 20">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </button>
                    )
                  )}
                </>
              ) : (
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-200">
                                    Template: {(() => {
                      // Try to get template name from current script template first
                      const currentTemplateName = availableScripts[currentScriptTemplate]?.name;
                      if (currentTemplateName) return currentTemplateName;
                                        
                      // Then try default template (for agents, this is team-assigned)
                      const defaultTemplateName = availableScripts[defaultTemplate]?.name;
                      if (defaultTemplateName) return defaultTemplateName;
                                        
                      // If we have a current script template but no name, show loading
                      if (currentScriptTemplate && currentScriptTemplate !== 'example-script') {
                        return 'Loading template...';
                      }
                                        
                      // Default fallback
                      return 'Example Script (Demo)';
                    })()}
                  </span>
                  {pendingTemplateAssignment && (
                    <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-md border border-orange-200 mt-1">
                                        ⏳ Next call: {pendingTemplateAssignment.templateName}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* AI Personalization Banner */}
          {currentScriptTemplate && availableScripts[currentScriptTemplate]?.aiPersonalization?.status === 'pending' && (
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-b border-blue-100 text-sm text-blue-700">
              <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Personalizing your script for your business...
            </div>
          )}

          {/* Step Progress Indicator */}
          {!(rightPanelContent === 'prospectData' || (rightPanelContent === 'script' && currentStage === 'call_end')) && stepProgress.total > 0 && (
            <div className="w-full flex items-center space-x-3 px-4 py-1 bg-white border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
                Step {stepProgress.current} of {stepProgress.total}
              </span>
              <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${Math.round((stepProgress.current / stepProgress.total) * 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 flex flex-row w-full h-full relative overflow-hidden" style={{ paddingTop: '15px' }}>

            {/* Script Content Column */}
            <div className={`flex-1 flex flex-col min-w-0 ${(rightPanelContent === 'prospectData' || (rightPanelContent === 'script' && currentStage === 'call_end')) ? 'overflow-y-auto pb-6' : 'overflow-hidden'}`}>

            {/* Conversation History - Top bar layout with buttons on the right */}
            {historyLayout === 'top' && !(currentStage === 'call_end' || currentStage === '__end_script__' || rightPanelContent === 'prospectData') && (
              <div className="w-full mb-0 px-4 flex items-stretch gap-2">
                {/* History box */}
                <div className="flex-1 bg-white rounded-md shadow-sm border border-gray-200 p-4 h-32 overflow-y-auto">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-600">Conversation History</h3>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-400">{conversationHistory.length} messages</span>
                      <button
                        onClick={toggleHistoryLayout}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 hover:border-blue-300 rounded transition-colors"
                        aria-label="Move history to side panel"
                        title="Move history to side panel"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
                        </svg>
                        Side Panel
                      </button>
                    </div>
                  </div>
                            
                  {conversationHistory.length === 0 ? (
                    <div className="flex items-center justify-center h-16 text-gray-400 text-sm">
                      Start the call to see conversation history
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {conversationHistory.map((entry, index) => (
                        <div 
                          key={index} 
                          className={`flex ${entry.role === 'Agent' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] px-3 py-2 rounded text-sm ${
                            entry.role === 'Agent' 
                              ? 'bg-blue-100 text-black' 
                              : 'bg-green-100 text-black'
                          }`}>
                            <span className="font-medium">{entry.role}:</span> {entry.text}
                          </div>
                        </div>
                      ))}
                      <div ref={conversationEndRef} />
                    </div>
                  )}
                </div>
                {/* Back/Reset buttons - stacked on the right of the history box */}
                <div className="flex flex-col justify-center gap-2 flex-shrink-0">
                  {previousStages.length > 0 && currentStage !== 'call_end' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleProspectResponse('back', 'action'); }}
                      aria-label="Go back to previous step"
                      icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>}
                    >
                      Back
                    </Button>
                  )}
                  <div title={conversationHistory.length === 0 ? 'Start a conversation first to enable reset' : 'Reset this call and start fresh'}>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowResetDialog(true); }}
                      disabled={conversationHistory.length === 0}
                      aria-label="Reset current call"
                      icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {rightPanelContent === 'script' && currentStage !== 'call_end' && (
              <>
                {/* Agent Script - Full width, left-aligned */}
                <div className="flex flex-col mb-6 px-4" style={{ marginTop: '20px' }}>
                  <div className="w-full min-h-48 max-h-96 bg-blue-50 rounded-md shadow-sm border border-blue-300 p-4 flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-600">Agent Script</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto text-left px-2">
                      <div className="text-xl md:text-2xl font-medium text-gray-800 leading-relaxed whitespace-pre-wrap">
                                        "{currentStageConfig?.script || 'No script available for this stage.'}"
                      </div>
                    </div>
                  </div>
                </div>

                {/* Response Options - Full width */}
                {currentStage !== 'call_end' && (
                  <div className="w-full flex flex-col items-center px-4">
                    {/* Check for open text responses */}
                    {(() => {
                      // Check if any button has type 'open_text'
                      const openTextButtons = currentStageConfig?.buttons?.filter(button => button.type === 'open_text') || [];
                                    
                      // Debug: Log the open text button data
                      console.log('🔍 Open text buttons found:', openTextButtons);
                      console.log('🔍 Current stage config:', currentStageConfig);
                                    
                      if (openTextButtons.length > 0) {
                        // Handle multiple open text responses
                        if (openTextButtons.length === 1) {
                          // Single open text response - use existing logic
                          const openTextButton = openTextButtons[0];
                          console.log('🔍 Single open text button:', openTextButton);
                          return (
                            <div className="bg-white border border-gray-200 rounded p-4 shadow-sm w-full max-w-5xl mx-auto mb-40">
                              <label className="block text-sm font-medium text-gray-700 mb-3">
                                {openTextButton.label || 'Enter your response:'}
                              </label>
                              <input
                                type="text"
                                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                placeholder={openTextButton.placeholder || 'Type your response here...'}
                                value={prospectInput}
                                onChange={(e) => setProspectInput(e.target.value)}
                              />
                              <div className="mt-3">
                                <Button
                                  variant="primary"
                                  size="md"
                                  fullWidth
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (prospectInput.trim()) {
                                      // Use the actual response key from the button
                                      handleProspectResponse(openTextButton.value, 'text');
                                    }
                                  }}
                                  disabled={!prospectInput.trim()}
                                >
                                                            Submit
                                </Button>
                              </div>
                            </div>
                          );
                        } else {
                          // Multiple open text responses - show form with all fields
                          return (
                            <div className="bg-white border border-gray-200 rounded p-4 shadow-sm space-y-4 w-full max-w-6xl mx-auto mb-32">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">
                                                        Please provide the following information:
                              </h4>
                              {openTextButtons.map((button, index) => (
                                <div key={button.value} className="space-y-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    {button.label || `Field ${index + 1}:`}
                                  </label>
                                  <input
                                    type="text"
                                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                    placeholder={button.placeholder || 'Type your response here...'}
                                    value={multipleInputs[button.value] || ''}
                                    onChange={(e) => setMultipleInputs(prev => ({
                                      ...prev,
                                      [button.value]: e.target.value
                                    }))}
                                  />
                                </div>
                              ))}
                              <div className="mt-4">
                                <Button
                                  variant="primary"
                                  size="md"
                                  fullWidth
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // Check if all required fields have content
                                    const allFieldsFilled = openTextButtons.every(button => 
                                      multipleInputs[button.value] && multipleInputs[button.value].trim()
                                    );
                                                                
                                    if (allFieldsFilled) {
                                      // Submit all responses at once
                                      handleMultipleOpenTextResponses(openTextButtons, multipleInputs);
                                    }
                                  }}
                                  disabled={!openTextButtons.every(button => 
                                    multipleInputs[button.value] && multipleInputs[button.value].trim()
                                  )}
                                >
                                                            Submit All Responses
                                </Button>
                              </div>
                            </div>
                          );
                        }
                      }
                                    
                      // Fallback to original text input if required
                      if (currentStageConfig?.requiresTextInput) {
                        return (
                          <div className="bg-white border border-gray-200 rounded p-4 shadow-sm">
                            <label className="block text-sm font-medium text-gray-700 mb-3">Prospect Response</label>
                            <textarea
                              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm resize-none"
                              rows="3"
                              placeholder="Type prospect's response here..."
                              value={prospectInput}
                              onChange={(e) => setProspectInput(e.target.value)}
                            ></textarea>
                          </div>
                        );
                      }
                                    
                      return null;
                    })()}

                    {/* Response Buttons - Compact Grid Layout */}
                    {(() => {
                      console.log('🔍 Button rendering debug:', {
                        currentStage,
                        isObjectionNode: currentStage?.startsWith('objection_'),
                        hasStageConfig: !!currentStageConfig,
                        hasButtons: !!currentStageConfig?.buttons,
                        buttonCount: currentStageConfig?.buttons?.length || 0,
                        buttons: currentStageConfig?.buttons?.map(b => ({ label: b.label, value: b.value, type: b.type })),
                        hasOpenText: !!currentStageConfig?.buttons?.find(button => button.type === 'open_text')
                      });
                      return null;
                    })()}
                    {!currentStageConfig?.buttons?.find(button => button.type === 'open_text') && (
                      <div className="flex flex-wrap justify-center gap-3">
                        {currentStageConfig?.buttons?.map((button, index) => (
                          <Button
                            key={index}
                            variant={
                              button.value === 'end_call' || button.value === 'end_call_no_notes' 
                                ? 'danger' 
                                : 'primary'
                            }
                            size="md"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleProspectResponse(button.value, button.type);
                            }}
                            disabled={
                              (button.value === 'submit_rent' || button.value === 'submit_time' || button.value === 'submit_notes') &&
                                                        currentStageConfig?.requiresTextInput &&
                                                        prospectInput.trim() === ''
                            }
                          >
                            {button.label}
                          </Button>
                        ))}
                      </div>
                    )}
                                
                    {/* Objection Response Buttons - Red buttons below regular responses */}
                    {currentStageConfig?.step && currentScriptTemplate && availableScripts && availableScripts[currentScriptTemplate]?.steps && (
                      (() => {
                        const currentStep = availableScripts[currentScriptTemplate].steps.find(step => step.id === currentStageConfig.step);
                        const objectionResponses = currentStep?.objectionResponses || [];
                                        
                        // Debug logging
                        console.log('🔍 Objection Debug:', 
                          'step:', currentStageConfig?.step,
                          'template:', currentScriptTemplate,
                          'objections:', objectionResponses.length
                        );
                                        
                        if (currentStep) {
                          console.log('🔍 Current step for objections:', currentStep.id, 'objections:', objectionResponses.length);
                        }
                                        
                        if (objectionResponses.length === 0) return null;
                                        
                        return (
                          <div className="mt-4">
                            <div className="text-center mb-3">
                              <span className="text-sm text-gray-600 font-medium">Common Objections:</span>
                            </div>
                            <div className="flex flex-wrap justify-center gap-3">
                              {objectionResponses.map((objection, index) => (
                                <Button
                                  key={index}
                                  variant="danger"
                                  size="md"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleProspectResponse(`objection_${objection.id}`, 'objection');
                                  }}
                                >
                                  {objection.label}
                                </Button>
                              ))}
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}
              </>
            )}

            {/* Call End Message and Lead Form */}
            {rightPanelContent === 'script' && currentStage === 'call_end' && (
              <div className="w-full max-w-2xl mx-auto">
                <div className="bg-white rounded-md shadow-md border border-gray-200 p-4 text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">Call Complete</h2>
                  <p className="text-sm text-gray-600">Great job! Please fill out the prospect information below.</p>
                </div>
              </div>
            )}

            {(rightPanelContent === 'prospectData' || (rightPanelContent === 'script' && currentStage === 'call_end')) && (
              <div className="w-full max-w-2xl mx-auto">
                <div className="text-center mb-3">
                  <h2 className="text-xl font-semibold text-gray-800 mb-1">{currentProspectFormConfig?.title || 'Lead Information'}</h2>
                  <p className="text-gray-600 text-sm">Please fill out the prospect details from your conversation.</p>
                </div>

                {prospectDataError && (
                  <div className="mb-4">
                    <ErrorMessage 
                      message={prospectDataError}
                      severity="error"
                    />
                  </div>
                )}

                <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
                  <div className="space-y-4">
                    {currentProspectFormConfig.fields.map((field) => (
                      <div key={field.id}>
                        <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-2">
                          {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {field.type === 'textarea' ? (
                          <textarea
                            id={field.id}
                            className="w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                            placeholder={field.placeholder}
                            value={prospectDataForm[field.id] || ''}
                            onChange={(e) => setProspectDataForm(prev => ({ ...prev, [field.id]: e.target.value }))}
                            rows={4}
                          />
                        ) : (
                          <input
                            type={field.type}
                            id={field.id}
                            className="w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                            placeholder={field.placeholder}
                            value={prospectDataForm[field.id] || ''}
                            onChange={(e) => setProspectDataForm(prev => ({ ...prev, [field.id]: e.target.value }))}
                          />
                        )}
                      </div>
                    ))}

                    {/* Call Outcome */}
                    <div>
                      <label htmlFor="callOutcome" className="block text-sm font-medium text-gray-700 mb-2">
                        Call Outcome
                      </label>
                      <select
                        id="callOutcome"
                        value={callOutcome}
                        onChange={(e) => setCallOutcome(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm bg-white text-sm"
                      >
                        <option value="">— Select outcome —</option>
                        <optgroup label="🔥 Hot">
                          <option value="Hot Lead">🔥 Hot Lead — Very interested, ready to move forward</option>
                        </optgroup>
                        <optgroup label="🌡️ Warm">
                          <option value="Warm Lead">🌡️ Warm Lead — Interested but needs follow-up</option>
                          <option value="Callback">📅 Callback — Asked to be called back</option>
                        </optgroup>
                        <optgroup label="❄️ Cold">
                          <option value="Cold Lead">❄️ Cold Lead — Not interested now, maybe later</option>
                          <option value="Not Interested">🚫 Not Interested — Declined</option>
                          <option value="Voicemail">📬 Voicemail — Left a voicemail</option>
                          <option value="Wrong Number">❌ Wrong Number — Wrong person or disconnected</option>
                          <option value="Do Not Call">⛔ Do Not Call — Asked not to be contacted</option>
                        </optgroup>
                      </select>
                    </div>

                    <Button
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={leadSaving}
                      disabled={leadSaving}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleProspectDataSubmit(e);
                      }}
                      className="mt-3"
                    >
                                    Save & Start New Call
                    </Button>
                  </div>
                </div>
              </div>
            )}

            </div>{/* End Script Content Column */}

            {/* Conversation History - Side drawer layout */}
            {historyLayout === 'side' && !(currentStage === 'call_end' || currentStage === '__end_script__' || rightPanelContent === 'prospectData') && (
              <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-white flex flex-col h-full">
                {/* Back/Reset buttons - side by side above the drawer */}
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
                  {previousStages.length > 0 && currentStage !== 'call_end' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleProspectResponse('back', 'action'); }}
                      aria-label="Go back to previous step"
                      icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>}
                    >
                      Back
                    </Button>
                  )}
                  <div title={conversationHistory.length === 0 ? 'Start a conversation first to enable reset' : 'Reset this call and start fresh'}>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowResetDialog(true); }}
                      disabled={conversationHistory.length === 0}
                      aria-label="Reset current call"
                      icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                  <h3 className="text-sm font-medium text-gray-600">History</h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-400">{conversationHistory.length}</span>
                    <button
                      onClick={toggleHistoryLayout}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 hover:border-blue-300 rounded transition-colors"
                      aria-label="Move history to top bar"
                      title="Move history to top bar"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                      Top Bar
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  {conversationHistory.length === 0 ? (
                    <div className="text-gray-400 text-sm text-center mt-8">
                      Start the call to see history
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {conversationHistory.map((entry, index) => (
                        <div key={index} className={`px-2 py-1.5 rounded text-xs ${
                          entry.role === 'Agent' 
                            ? 'bg-blue-50 text-gray-800' 
                            : 'bg-green-50 text-gray-800'
                        }`}>
                          <span className="font-medium">{entry.role}:</span> {entry.text}
                        </div>
                      ))}
                      <div ref={conversationEndRef} />
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>

          {/* Coaching Footnote - Fixed at bottom - Hidden during lead data collection */}
          {isTemplateReady && currentStageConfig?.coaching && !(currentStage === 'call_end' || currentStage === '__end_script__' || rightPanelContent === 'prospectData') && (
            <div className="fixed bottom-0 left-56 right-0 z-40" style={{ bottom: '10px' }}>
              <div className="flex justify-center px-4">
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r-lg shadow-sm max-w-5xl">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-4 h-4 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm text-yellow-800 leading-relaxed">{currentStageConfig.coaching}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
            
      {/* Reset Dialog - High z-index to ensure visibility */}
      {showResetDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reset Call?</h3>
            <p className="text-gray-600 mb-6">
                            Are you sure you want to reset this call? This will clear all conversation history and start fresh.
            </p>
            <div className="flex space-x-3 justify-end">
              <Button
                variant="outline"
                size="md"
                onClick={() => {
                  console.log('🔄 Reset cancelled by user');
                  setShowResetDialog(false);
                }}
              >
                                Cancel
              </Button>
              <Button
                variant="danger"
                size="md"
                onClick={() => {
                  console.log('🔄 Reset confirmed by user');
                  resetApp(false);
                  setShowResetDialog(false);
                }}
              >
                                Reset Call
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallScript;

